<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  value: {
    type: [Number, String],
    required: true,
  },
  percentage: {
    type: [Number, String],
    default: null,
  },
  numerator: {
    type: [Number, String],
    default: null,
  },
  denominator: {
    type: [Number, String],
    default: null,
  },
  changePct: {
    type: Number,
    default: null,
  },
  previousValue: {
    type: [Number, String],
    default: null,
  },
  infoText: {
    type: String,
    default: '',
  },
  inverseTrend: {
    type: Boolean,
    default: false, // If true, an increase is bad (e.g., abandoned calls)
  },
  unit: {
    type: String,
    default: '',
  },
});

const formattedValue = computed(() => {
  if (typeof props.value === 'number') {
    return props.value.toLocaleString();
  }
  return props.value ?? '0';
});

const isTrendingUp = computed(() => {
  if (props.changePct === null || props.changePct === undefined) return null;
  return props.changePct > 0;
});

const isTrendingNeutral = computed(() => {
  if (props.changePct === null || props.changePct === undefined) return true;
  return props.changePct === 0;
});

const trendBadgeColor = computed(() => {
  if (isTrendingNeutral.value) {
    return 'bg-n-slate-3 text-n-slate-11';
  }
  const isPositive = props.inverseTrend ? !isTrendingUp.value : isTrendingUp.value;
  return isPositive ? 'bg-n-teal-3 text-n-teal-11' : 'bg-n-ruby-3 text-n-ruby-11';
});

const trendIcon = computed(() => {
  if (isTrendingNeutral.value) return 'i-lucide-minus';
  return isTrendingUp.value ? 'i-lucide-arrow-up-right' : 'i-lucide-arrow-down-right';
});
</script>

<template>
  <div
    class="relative flex flex-col justify-between p-4 bg-n-surface-1 border border-n-strong rounded-xl shadow-sm hover:border-n-slate-6 transition-colors"
  >
    <div class="flex items-start justify-between gap-2 mb-2">
      <div class="flex items-center gap-1.5 text-xs font-medium text-n-slate-11 uppercase tracking-wider">
        <span>{{ label }}</span>
        <span
          v-if="infoText"
          v-tooltip="infoText"
          class="inline-flex cursor-help text-n-slate-10 hover:text-n-slate-12"
        >
          <Icon icon="i-lucide-info" class="w-3.5 h-3.5" />
        </span>
      </div>

      <!-- Trend Badge -->
      <div
        v-if="changePct !== null && changePct !== undefined"
        :class="[trendBadgeColor, 'inline-flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-semibold rounded-md']"
        :title="previousValue !== null ? `Previous period: ${previousValue}` : ''"
      >
        <Icon :icon="trendIcon" class="w-3 h-3" />
        <span>{{ Math.abs(changePct) }}%</span>
      </div>
    </div>

    <div class="flex items-baseline gap-2">
      <span class="text-2xl font-semibold tracking-tight text-n-slate-12">
        {{ formattedValue }}<span v-if="unit" class="text-lg font-normal text-n-slate-11 ml-0.5">{{ unit }}</span>
      </span>

      <!-- Percentage with numerator/denominator breakdown -->
      <span
        v-if="percentage !== null && percentage !== undefined"
        class="text-xs font-medium text-n-slate-11"
      >
        ({{ percentage }}%<span v-if="numerator !== null && denominator !== null"> · {{ numerator }}/{{ denominator }}</span>)
      </span>
    </div>

    <!-- Previous Period Comparison Footer -->
    <div
      v-if="previousValue !== null && previousValue !== undefined"
      class="mt-2 text-[11px] text-n-slate-10 flex items-center justify-between"
    >
      <span>Prev: {{ previousValue.toLocaleString ? previousValue.toLocaleString() : previousValue }}</span>
      <span v-if="changePct !== null && changePct !== undefined">
        {{ changePct > 0 ? '+' : '' }}{{ changePct }}%
      </span>
    </div>
  </div>
</template>
